import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { PlanStep } from '../../../services/PlanStep';
import { StepComponent } from '../../step.component';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { ObjectiveComponent } from './objective/objective.component';
import { InitiativeComponent } from './initiative/initiative.component';
import { Dialog } from 'primeng/primeng';
import { AppLoadingService } from '../../../../app-loading-panel/app-loading.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs/index';
import { InitiativeService } from '../../../services/initiative.service';
import { take } from 'rxjs/internal/operators';
import { ICriticalInitiativeSort } from '../../../services/ICriticalInitiativeSort';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PriorityService } from '../../../services/priority.service';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import { StrtObjective } from '../../../../shared/models/plan/StrtObjective';
import { StrtInitiative } from '../../../../shared/models/plan/StrtInitiative';
import { ObjectiveService } from '../../../services/objective.service';
import { DataUtils } from '../../../../shared/utilities/data-utils';
import { CitationExpansion } from '../../../../shared/models/ui/CitationExpansion';
import { ResourceService } from '../../../services/resource.service';
declare var $: any;

@Component({
  selector: 'app-creating-objectives-critical-initiatives',
  templateUrl: './creating-objectives-critical-initiatives.component.html',
  styleUrls: ['./creating-objectives-critical-initiatives.component.scss']
})
export class CreatingObjectivesCriticalInitiativesComponent
  extends StepComponent<StrtObjective|StrtInitiative>
  implements OnInit, OnDestroy, AfterViewChecked {

  private static readonly MAX_OBJECTIVE_COUNT = 10;
  private readonly MAX_INITIATIVES = 10;

  @ViewChild(ObjectiveComponent) private objectiveComp: ObjectiveComponent;
  @ViewChild(InitiativeComponent) private initiativeComp: InitiativeComponent;
  @ViewChild('confirmDeleteObjectiveDialog') private confirmDeleteObjectiveDialog: Dialog;
  @ViewChild('confirmDeleteInitiativeDialog') private confirmDeleteInitiativeDialog: Dialog;
  @ViewChild('confirmDeleteCitationDialog') private confirmDeleteCitationDialog: Dialog;
  @Output() initiativeSortFail = new EventEmitter<PlanCoordinate>();
  @Output() reloadInitiatives = new EventEmitter<PlanCoordinate>();

  @Input() strategyId: number;

  private objectiveItemToDelete: StrtObjective;
  private initiativeItemToDelete: StrtInitiative;

  private _reorderInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  reorderInProgress$ = this._reorderInProgress.asObservable();
  private _reorderFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  reorderFailed$ = this._reorderFailed.asObservable();
  reorderParentId: number;
  private forceScrollLocation = false;
  private forceScrollDataRefreshed = false;
  private resetFailedMsgOnLoad = false;
  private lastKnownScrollPosition = 0;

  private _prioritySubscription: Subscription;

  isLoading$: Observable<boolean>;

  private readonly defaultExpandedLocation: CitationExpansion = {
    initiative: null,
    objective: null,
    citation: null,
    isEdit: false,
    isDelete: false,
    isCreate: false
  };

  private _expandedLocationBS: BehaviorSubject<CitationExpansion> = new BehaviorSubject<CitationExpansion>(this.defaultExpandedLocation);
  expandedLocation$ = this._expandedLocationBS.asObservable();
  private _lastKnownExpandedLocation: CitationExpansion;

  private priorities: StrtPriority[] = [];

  constructor(private _loadingService: AppLoadingService,
              private _initiativeService: InitiativeService,
              private _priorityService: PriorityService,
              private _domSanitizer: DomSanitizer,
              private _strtDataStore: StrategiesDataStoreService,
              private _objectiveService: ObjectiveService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepBy(PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES), _resourceService);
  }

  ngOnInit(): void {
    this._loadingService.setLoading();
    this.isLoading$ = this._loadingService.isLoading$;
    this.expandedLocation$.subscribe((citationExpansion: CitationExpansion) => {
      this._lastKnownExpandedLocation = citationExpansion;
    });

    this._prioritySubscription = this._strtDataStore.priorities$.subscribe((priorities: StrtPriority[]) => {
      this.priorities = priorities;
      this._loadingService.clearLoading();
      if (this.forceScrollLocation) {
        this.forceScrollDataRefreshed = true;
      }
      if (this.resetFailedMsgOnLoad) {
        this.resetFailedMsgOnLoad = false;
        this._reorderFailed.next(false);
      }
    });
    this._priorityService.fetchPriorities(this.strategyId);
  }

  ngOnDestroy(): void {
    if (this._prioritySubscription) {
      this._prioritySubscription.unsubscribe();
    }
    this.priorities = [];
  }

  ngAfterViewChecked(): void {
    if (this.forceScrollLocation && this.forceScrollDataRefreshed) {
      this.forceScrollLocation = false;
      this.forceScrollDataRefreshed = false;
      setTimeout(() => {
        $('.main-content')[0].scrollTop = this.lastKnownScrollPosition;
        this.lastKnownScrollPosition = 0;
      });
    }
  }

  editInitiativeCitation(initiative: StrtInitiative, objective: StrtObjective) {
    this._expandedLocationBS.next({
      initiative: initiative.initiativeId,
      objective: objective.objectiveId,
      citation: initiative.citation,
      isEdit: true,
      isDelete: false,
      isCreate: false
    });
  }

  protected setPlanCoordinate(planCoordinate: PlanCoordinate): void {
    super.setPlanCoordinate(planCoordinate);
    // Every time plan coordinate changes, just reset the expanded row back to nothing
    this.resetExpandedInitiative();
  }

  private resetExpandedInitiative() {
    this._expandedLocationBS.next(this.defaultExpandedLocation);
  }

  get hasPrioritiesWithObjectives(): boolean {
    return this.priorities.some((priority: StrtPriority) => {
      return priority.objectives.length > 0;
    });
  }

  get planHasPriorities(): boolean {
    return this.priorities && this.priorities.length > 0;
  }

  get prioritiesWithObjectives() {
    return this.priorities.filter((priority: StrtPriority) => {
      return priority.objectives.length > 0;
    });
  }

  get isAddObjectivesDisabled() {
    return this.isAddItemDisabled ||
      this.priorities.every(it => it.objectives.length >= CreatingObjectivesCriticalInitiativesComponent.MAX_OBJECTIVE_COUNT);
  }

  onClickAddObjectives() {
    this.objectiveComp.createObjective(this.prioritiesWithObjectiveCapacity);
  }

  onClickEditObjective(objective: StrtObjective) {
    this.objectiveComp.editObjective(objective, this.prioritiesForEdit(objective));
  }

  initiativeIsNested(initiativeIndex: number, objectiveIndex: number, objective: StrtObjective, priority: StrtPriority) {
    return initiativeIndex === objective.initiatives.length - 1 && objectiveIndex !== priority.objectives.length - 1;
  }

  objectiveIsNested(objectiveIndex: number, objectives: StrtObjective[]) {
    return objectiveIndex !== 0 && objectives[objectiveIndex - 1].initiatives.length > 0;
  }

  private get prioritiesWithObjectiveCapacity() {
    return this.priorities.filter((priority: StrtPriority) => {
      return priority.objectives.length < CreatingObjectivesCriticalInitiativesComponent.MAX_OBJECTIVE_COUNT;
    });
  }

  private prioritiesForEdit(objective: StrtObjective): StrtPriority[] {
    // if they've passed an objective, the objective's priority needs to be in the list regardless
    return this.priorities.filter((priority: StrtPriority) => {
      return priority.objectives.length < CreatingObjectivesCriticalInitiativesComponent.MAX_OBJECTIVE_COUNT ||
             priority.priorityId === objective.priorityId;
    });
  }

  /* ~~~~~~~~~ Initiative Code ~~~~~~~~~ */

  get isInitiativeActive() {
    return this.initiativeComp.isActive;
  }

  set isInitiativeActive(isActive) {
    this.initiativeComp.isActive = isActive;
  }

  isAddInitiativesDisabled(objective: StrtObjective): boolean {
    return this.isAddItemDisabled || objective.initiatives.length >= this.MAX_INITIATIVES;
  }

  onClickAddInitiative(objective: StrtObjective) {
    this.initiativeComp.createInitiative(objective);
  }

  onClickEditInitiative(initiative: StrtInitiative, objective: StrtObjective) {
    this.initiativeComp.editInitiative(objective, initiative);
  }

  onAddCitation(initiative: StrtInitiative, objective: StrtObjective) {
    this._expandedLocationBS.next({
      initiative: initiative.initiativeId,
      objective: objective.objectiveId,
      citation: initiative.citation || null,
      isEdit: false,
      isDelete: false,
      isCreate: true
    });
  }

  onCitationChanged(data: any) {
    const updatedCitation = JSON.parse(JSON.stringify(this._lastKnownExpandedLocation));
    updatedCitation.citation = data ?
                               (data.trim() === '' ? null : data) :
                               null;
    setTimeout(() => {
      this._expandedLocationBS.next(updatedCitation);
    });
  }

  onCitationCancel() {
    this.resetExpandedInitiative();
  }

  isCitationSaveDisabled(): boolean {
    return !this._lastKnownExpandedLocation.citation || this._lastKnownExpandedLocation.citation.trim() !== '';
  }

  get lastKnownExpandedLocation(): CitationExpansion {
    return this._lastKnownExpandedLocation;
  }

  isCitationAddDisabled(initiative: StrtInitiative): boolean {
    return (initiative.citation && initiative.citation.trim() !== '') ||
           (this._lastKnownExpandedLocation.initiative === initiative.initiativeId);
  }


  onCitationDelete(initiative: StrtInitiative, objective: StrtObjective) {
    this._expandedLocationBS.next({
      initiative: initiative.initiativeId,
      objective: objective.objectiveId,
      citation: initiative.citation,
      isEdit: false,
      isDelete: true,
      isCreate: false
    });
    this.confirmCitationDialogVisible = true;
  }

  shouldShowRTE(initiative: StrtInitiative): boolean {
    return !this._lastKnownExpandedLocation.isDelete && this._lastKnownExpandedLocation.initiative
        && (this._lastKnownExpandedLocation.initiative === initiative.initiativeId);
  }

  shouldKillHover(initiative: StrtInitiative): boolean {
    return (this._lastKnownExpandedLocation.isEdit || this._lastKnownExpandedLocation.isCreate)
      && this._lastKnownExpandedLocation.initiative !== null
      && (this._lastKnownExpandedLocation.initiative === initiative.initiativeId);
  }

  shouldShowCitationView(initiative: StrtInitiative): boolean {
    const givenCitationHasData = initiative.citation !== null && initiative.citation !== '';
    const initiativeMatchesExpand = this._lastKnownExpandedLocation.initiative === initiative.initiativeId;
    const isEditMode = this._lastKnownExpandedLocation.isEdit;
    const isCreateMode = this._lastKnownExpandedLocation.isCreate;
    return initiativeMatchesExpand ? !(isEditMode || isCreateMode) : givenCitationHasData;
  }

  onConfirmInitiativeDeleteDialogAccept() {
    this.confirmInitiativeDialogVisible = false;
    this.onInitiativeDelete(this.initiativeItemToDelete);
  }

  onClickDeleteObjectiveItem(item: StrtObjective) {
    this.objectiveItemToDelete = item;
    this.confirmObjectiveDialogVisible = true;
  }

  onClickDeleteInitiativeItem(item: StrtInitiative) {
    this.initiativeItemToDelete = item;
    this.confirmInitiativeDialogVisible = true;
  }

  onItemError(error: Error) {
    this.isInitiativeActive = false;
    super.onItemError(error);
  }

  protected get confirmObjectiveDialogVisible() {
    return this.confirmDeleteObjectiveDialog && this.confirmDeleteObjectiveDialog.visible;
  }

  protected set confirmObjectiveDialogVisible(isVisible: boolean) {
    if (this.confirmDeleteObjectiveDialog) {
      this.confirmDeleteObjectiveDialog.visible = isVisible;
    }
  }


  protected get confirmCitationDialogVisible() {
    return this.confirmDeleteCitationDialog.visible || false;
  }
  protected set confirmCitationDialogVisible(isVisible: boolean) {
    if (this.confirmDeleteCitationDialog) {
      this.confirmDeleteCitationDialog.visible = isVisible;
    }
  }

  protected get confirmInitiativeDialogVisible() {
    return this.confirmDeleteInitiativeDialog && this.confirmDeleteInitiativeDialog.visible;
  }

  protected set confirmInitiativeDialogVisible(isVisible: boolean) {
    if (this.confirmDeleteInitiativeDialog) {
      this.confirmDeleteInitiativeDialog.visible = isVisible;
    }
  }

  onRowReorder(objective: StrtObjective, event: any) {
    if (event.dragIndex === event.dropIndex) {
      // exit since the row didn't actually move
      return;
    }
    this.reorderParentId = objective.objectiveId;
    this._reorderInProgress.next(true);
    try {
      // Get new order of ids
      const newOrder: number[] = objective.initiatives.map((initiative: StrtInitiative) => {
        return initiative.initiativeId;
      });
      // Copy new order as old order
      let oldOrder: number[] = JSON.parse(JSON.stringify(newOrder));
      // Extract ID of moved record
      const directionUpIndex = event.dropIndex > event.dragIndex;
      const directionModifier = directionUpIndex ? 1 : 0;
      const movedId = oldOrder[event.dropIndex >= oldOrder.length ? oldOrder.length - 1 : event.dropIndex - directionModifier];
      // Remove moved record from old order
      oldOrder = oldOrder.filter((id: number) => {
        return id !== movedId;
      });
      // Reinsert old record at original index
      oldOrder.splice(event.dragIndex, 0, movedId);
      const sortPayload: ICriticalInitiativeSort = {
        objectiveId: objective.objectiveId,
        strategyId: this.strategyId,
        originalInitiativeIds: oldOrder,
        sortedInitiativeIds: newOrder
      };
      this._initiativeService.sortInitiatives(sortPayload)
        .pipe(take(1))
        .subscribe((response: any) => {
          this._reorderInProgress.next(false);
          this.lastKnownScrollPosition = $('.main-content')[0].scrollTop;
          this.forceScrollDataRefreshed = false;
          this.forceScrollLocation = true;
        }, (err) => {
          this._reorderFailed.next(true);
          this.lastKnownScrollPosition = $('.main-content')[0].scrollTop;
          this.forceScrollDataRefreshed = false;
          this.forceScrollLocation = true;
          setTimeout(() => {
            this.initiativeSortFail.emit(this.planCoordinate);
            this.resetFailedMsgOnLoad = true;
            this._priorityService.fetchPriorities(this.strategyId);
            this._reorderInProgress.next(false);
          }, 3000);
        });
    } catch (err) {
      console.error(`Caught outer error on critical initiative reorder: `, err);
      this._reorderFailed.next(true);
      setTimeout(() => {
        this.initiativeSortFail.emit(this.planCoordinate);
        this._priorityService.fetchPriorities(this.strategyId);
        this._reorderInProgress.next(false);
      }, 3000);
    }

  }

  sanitize(html: string): SafeHtml {
    return this._domSanitizer.bypassSecurityTrustHtml(html);
  }

  /****************************************************************
   * Service Layer Integrations
   ****************************************************************/

  /**
   * Creates an objective
   * @param {StrtObjective} pendingObjective
   */
  onObjectiveCreate(pendingObjective: StrtObjective) {
    this._loadingService.setLoading();
    pendingObjective.strategyId = this.strategyId;
    this._objectiveService.createObjective(pendingObjective)
      .subscribe((newObjective: StrtObjective) => {
        this._strtDataStore.updateObjective(newObjective, this.priorities);
      }, (err) => {
        this._loadingService.clearLoading();
        console.error(`On Objective Create Error: `, err);
      });
  }

  /**
   * Edits an objective
   * @param {StrtObjective} editObjective
   */
  onObjectiveEdit(editObjective: StrtObjective) {
    this._loadingService.setLoading();
    this._objectiveService.updateObjective(editObjective)
      .subscribe((updatedObjective: StrtObjective) => {
        updatedObjective.initiatives = editObjective.initiatives;
        this._strtDataStore.updateObjective(updatedObjective, this.priorities);
      }, (err) => {
        this._loadingService.clearLoading();
        console.error(`On Objective Edit Error: `, err);
      });
  }

  /**
   * Deletes an initiative
   * @param {StrtInitiative} initiative
   */
  onInitiativeDelete(initiative: StrtInitiative) {
    this._loadingService.setLoading();
    this._initiativeService.deleteInitiative(initiative).pipe(take(1)).subscribe((result: {}) => {
      Object.assign(initiative, {objectiveId: undefined});
      this._strtDataStore.updateInitiative(initiative, this.priorities);
    }, (err) => {
      console.error(`Error deleting initiative: `, err);
      this._loadingService.clearLoading();
    });
  }

  /**
   * Updates the data store, new initiative already created
   * @param {StrtInitiative} initiative
   */
  onInitiativeCreate(initiative: StrtInitiative) {
    this._strtDataStore.updateInitiative(initiative, this.priorities);
  }

  /**
   * Updates the data store, initiative already edited
   * @param {StrtInitiative} initiative
   */
  onInitiativeEdit(initiative: StrtInitiative) {
    this._strtDataStore.updateInitiative(initiative, this.priorities);
  }

  /**
   * Deletes a specific citation
   */
  onConfirmCitationDelete() {
    this.expandedLocation$.subscribe((citationExpansion: CitationExpansion) => {
      if (!citationExpansion.isDelete) {
        return;
      }
      this._loadingService.setLoading();
      this._initiativeService.deleteCitation(this.strategyId, citationExpansion.objective, citationExpansion.initiative)
        .subscribe((result) => {
          this.confirmCitationDialogVisible = false;
          const parentInitiative = DataUtils.create(this.priorities).getInitiativePathById(citationExpansion.initiative).initiative;
          parentInitiative.citation = null;
          this._strtDataStore.updateInitiative(parentInitiative, this.priorities);
          this.resetExpandedInitiative();
        }, (err) => {
          console.error(`On Delete Citation ERROR: `, err);
          this._loadingService.clearLoading();
          // TODO: Add error message
        });
    });
  }

  /**
   * Deletes an objective
   */
  onConfirmObjectiveDeleteDialogAccept() {
    this._loadingService.setLoading();
    this.confirmObjectiveDialogVisible = false;
    this._objectiveService.deleteObjective(this.objectiveItemToDelete)
      .subscribe(() => {
        const objective: StrtObjective = Object.assign(this.objectiveItemToDelete, {priorityId: undefined});
        this._strtDataStore.updateObjective(objective, this.priorities);
      }, (err) => {
        this._loadingService.clearLoading();
        console.error(`Failed to delete objective: `, err);
      });
  }

  /**
   * Saves a citation
   */
  onCitationSave() {
    this.expandedLocation$.pipe(take(1)).subscribe((citationExpansion: CitationExpansion) => {

      let observable: Observable<any>;
      if (citationExpansion.isEdit) {
        observable = this._initiativeService.editCitation(this.strategyId,
          citationExpansion.objective,
          citationExpansion.initiative,
          citationExpansion.citation);
      } else {
        observable = this._initiativeService.createCitation(this.strategyId,
          citationExpansion.objective,
          citationExpansion.initiative,
          citationExpansion.citation);
      }
      if (observable) {
        this._loadingService.setLoading();
        observable.pipe(take(1))
          .subscribe((result) => {
            const initiative: StrtInitiative = DataUtils.create(this.priorities)
              .getInitiativePathById(citationExpansion.initiative).initiative;
            initiative.citation = citationExpansion.citation;
            this._strtDataStore.updateInitiative(initiative, this.priorities);
            this.resetExpandedInitiative();
          }, (err) => {
            console.error(`On Citation Save/Edit Err: `, err);
            this._loadingService.clearLoading();
          });
      }

    });
  }
}
